/**
 * Shared types for the Profile Activity (the /profile route). Mirrors the live DB tables
 * `member_profiles` (free, editable basics + supporter flag) and `member_preferences`
 * (cuisines, Brockett voice dials, reviewer voice, notebook, privacy toggles).
 *
 * Tiering: voice dials, the notebook (observations + notes) and custom_review_voice are
 * SUPPORTER-ONLY. Everything else is free + editable for everyone. The server enforces the
 * tier — these types just describe the wire shape.
 */

/** A handwritten note the member leaves for Brockett ("your notes for Brockett"). */
export interface NotebookNote {
  id: string;
  text: string;
  added_at: string;
}

/** An auto-observation Brockett "remembers" about the member ("what Brockett remembers"). */
export interface NotebookObservation {
  id: string;
  text: string;
  observed_at?: string;
}

/** Free, editable basics — backed by `member_profiles`. */
export interface Profile {
  preferred_name: string;
  zip_code: string;
  price_range: string;
  sports_teams: string[];
  dietary_avoidances: string[];
  fav_cuisines: string[];
  tracking_enabled: boolean;
}

/** Preferences — backed by `member_preferences`. Voice dials / notebook / custom voice are supporter-only. */
export interface Preferences {
  liked_cuisines: string[];
  disliked_cuisines: string[];
  // Brockett voice dials (1-5). Supporter-only.
  voice_humor: number;
  voice_local_pride: number;
  voice_formality: number;
  voice_detail: number;
  voice_adventure: number;
  // Reviewer voice.
  review_voice: string;
  custom_review_voice: string; // supporter-only
  // Brockett's Notebook. Supporter-only.
  notebook_observations: NotebookObservation[];
  notebook_notes: NotebookNote[];
  // Privacy toggles.
  profile_private: boolean;
  allow_member_dms: boolean;
  notify_my_reviews: boolean;
}

/** Response from GET /api/profile. */
export interface ProfileResponse {
  is_supporter: boolean;
  profile: Profile;
  preferences: Preferences;
}

/** Body for PUT /api/profile — any subset of the FREE fields (server ignores supporter-only keys). */
export interface ProfileUpdate {
  preferred_name?: string;
  zip_code?: string;
  price_range?: string;
  sports_teams?: string[];
  dietary_avoidances?: string[];
  fav_cuisines?: string[];
  liked_cuisines?: string[];
  disliked_cuisines?: string[];
  review_voice?: string;
  tracking_enabled?: boolean;
  profile_private?: boolean;
  allow_member_dms?: boolean;
  notify_my_reviews?: boolean;
}

/** Body for PUT /api/profile/voice — SUPPORTER-ONLY. */
export interface VoiceUpdate {
  voice_humor?: number;
  voice_local_pride?: number;
  voice_formality?: number;
  voice_detail?: number;
  voice_adventure?: number;
  custom_review_voice?: string;
}

/** Body for PUT /api/profile/notebook — SUPPORTER-ONLY. */
export interface NotebookUpdate {
  add_note?: string;
  remove_note_id?: string;
}

/** The five Brockett voice dial keys (each a smallint 1-5). */
export const VOICE_DIAL_KEYS = [
  'voice_humor',
  'voice_local_pride',
  'voice_formality',
  'voice_detail',
  'voice_adventure',
] as const;
export type VoiceDialKey = (typeof VOICE_DIAL_KEYS)[number];

/** Max member-authored notes kept in notebook_notes. */
export const MAX_NOTEBOOK_NOTES = 10;
